import type { MailFolderId } from "./MailFolderRail";
import type { MailThreadAction } from "./mailApi";

/** True when a mail shortcut should not fire (typing, compose, dialogs). */
export function shouldIgnoreMailKeyboardShortcut(
  event: KeyboardEvent,
  options?: { composeOpen?: boolean; allowModEnter?: boolean },
): boolean {
  if (options?.composeOpen) return true;

  const isModEnter =
    Boolean(options?.allowModEnter) &&
    event.key === "Enter" &&
    (event.metaKey || event.ctrlKey);
  if (!isModEnter && (event.metaKey || event.ctrlKey || event.altKey)) {
    return true;
  }

  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;

  if (target.isContentEditable) return true;
  if (target.closest("[contenteditable='true']")) return true;

  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.closest('[role="dialog"]')) return true;

  return false;
}

export function isMailTrashShortcutKey(key: string): boolean {
  return key === "#" || key === "Delete";
}

/** Cmd/Ctrl+Enter — used in compose; allowed through ignore helper when opted in. */
export function isMailSendShortcut(event: KeyboardEvent): boolean {
  return event.key === "Enter" && (event.metaKey || event.ctrlKey);
}

/**
 * List `#` / Delete action. Spam uses trash (same as bulk), not not_spam.
 * Trash folder deletes forever.
 */
export function resolveMailListTrashAction(
  folder: MailFolderId,
): Extract<MailThreadAction, "trash" | "delete_forever"> {
  if (folder === "trash") return "delete_forever";
  return "trash";
}

export function resolveMailListIndex(
  threadsLength: number,
  currentIndex: number,
  delta: 1 | -1,
): number | null {
  if (threadsLength <= 0) return null;
  if (currentIndex < 0) {
    return delta === 1 ? 0 : threadsLength - 1;
  }
  const next = currentIndex + delta;
  if (next < 0 || next >= threadsLength) return null;
  return next;
}
